import { Reducer } from 'redux';
import { ActionType, getType } from 'typesafe-actions';
import { cardReducer } from '../Card/CardReducer';
import { CardActions } from '../Card/CardActions';
import { LevelActions } from './LevelActions';
import LevelStateHelper, { isLevelComplete } from './LevelStateHelper';
import { FTUXStateHelper } from '../Game/FTUX/FTUXStateHelper';
import { LevelTask } from './LevelTask';
import { RewardType } from '../../Utility/RewardHelper';
import { checkHelperFriendVisible } from './HelperFriend/HelperFriendStateHelper';
import { sendEvent, track, TrackMethod } from '../../Utility/Tracking/Track';
import { MissionTrackingEventName } from '../../Utility/Tracking/TrackingEvent';
import { LevelStatus } from './LevelStatus';
import { IGameState } from '../Game/IGameState';
import UIStateHelper from '../UI/UIStateHelper';
import { ProgressStateHelper } from '../Player/Progress/ProgressStateHelper';
import { InventoryStateHelper } from '../Player/Inventory/InventoryStateHelper';
import { FTUX_LEVEL_TASKS } from 'Constants/FTUX';

const tasksToClear: LevelTask[] = [LevelTask.FTUX_GOLD];
export const LevelReducer: Reducer<IGameState> = (
    state: IGameState,
    action: ActionType<typeof LevelActions | typeof CardActions>
): IGameState => {
    state = {
        ...state,
        levelState: {
            ...state.levelState,
            tasks: state.levelState.tasks.filter(task => !tasksToClear.includes(task.task)),
        },
    };

    switch (action.type) {
        case getType(LevelActions.setLevelState):
            state = {
                ...state,
                levelState: {
                    ...state.levelState,
                    status: LevelStatus.LOADED,
                    ...action.payload,
                },
            };

            state = LevelStateHelper.getCoinRewardAmount(state);
            state = LevelStateHelper.ensureBoosterUses(state);
            state = LevelStateHelper.getBoosterPrices(state);
            state = LevelStateHelper.useExtraCardBoosters(state);

            // FTUX tasks and level unlocks.
            state = FTUXStateHelper.setLevelTasks(state);
            state = FTUXStateHelper.setLevelComponents(state);

            track('mLevelMoves', state.levelState.cardsState.inStack.length);

            break;
        case getType(LevelActions.levelIntro):
            if (state.levelState.status === LevelStatus.LOADED) {
                state = {
                    ...state,
                    levelState: {
                        ...state.levelState,
                        status: LevelStatus.INTRO,
                    },
                };
                track('mPlus5Used', 0, TrackMethod.SET);
                track('mMassRemoveUsed', 0, TrackMethod.SET);
                track('mRemoveOneUsed', 0, TrackMethod.SET);
                track('mChangerUsed', 0, TrackMethod.SET);
                track('mJokerUsed', 0, TrackMethod.SET);
                track('mUndoUsed', 0, TrackMethod.SET);
                track('mCoinsSpent', 0, TrackMethod.SET);
                track('mExtraCardsBought', 0, TrackMethod.SET);
                track('mExtraCardsWon', 0, TrackMethod.SET);
                track('mJokerCardsWon', 0, TrackMethod.SET);
                track('mStreakNum', 0, TrackMethod.SET);
                track('mStreakColorNum', 0, TrackMethod.SET);
                track('mHighestPlayStreak', 0, TrackMethod.SET);
                track('mNumBoostersUsedLossAversion', 0, TrackMethod.SET);
                track('mMovesMade', 0, TrackMethod.SET);
                track('mLossAversionCoinsSpent', 0, TrackMethod.SET);
                track('mCoinsBoughtWithAds', 0, TrackMethod.SET);

                const level = ProgressStateHelper.getLevelData(
                    state,
                    state.levelState.levelNumber,
                    state.levelState.levelType
                );
                const hasWonLevel = Boolean(level?.stars);
                track('mIsSuccessRetry', Number(hasWonLevel), TrackMethod.SET);

                const starsToGain = Math.max(3 - (level?.stars ?? 0), 0);
                track('mChestStarsToGain', starsToGain, TrackMethod.SET);

                //Check which FTUX needs to be executed
                state = LevelStateHelper.queueFTUXTasksIfNeeded(state);
            }
            break;
        case getType(LevelActions.levelActive):
            if (state.levelState.status === LevelStatus.INTRO) {
                // Add level to list to account for an attempt at the level
                const levels = state.playerState.levels[state.levelState.levelType].slice();
                const currentLevel = ProgressStateHelper.getLevelData(
                    state,
                    state.levelState.levelNumber,
                    state.levelState.levelType
                );

                if (currentLevel === undefined) {
                    levels.push({
                        levelNumber: state.levelState.levelNumber,
                        stars: undefined,
                    });
                }

                // Charge for level once level becomes active
                if (state.levelState.isFreePlay) {
                    track('tFreeEntryUsed', 1, TrackMethod.ADD);
                    track('mFreeEntryUsed', 1, TrackMethod.ADD);

                    state = {
                        ...state,

                        playerState: InventoryStateHelper.setBoosterFreePlay(
                            state.playerState,
                            state.playerState.inventory[RewardType.FREE_PLAY] - 1,
                            { queueTask: false }
                        ),
                    };
                }
                state = {
                    ...state,
                    playerState: InventoryStateHelper.setCoins(
                        state.playerState,
                        state.playerState.inventory.coins - state.levelState.levelCost
                    ),
                };

                state = {
                    ...state,
                    levelState: {
                        ...state.levelState,
                        status: LevelStatus.ACTIVE,

                        // Reset undo history/count at the start of each level.
                        cardsState: {
                            ...state.levelState.cardsState,
                            undoHistory: [],
                        },
                    },
                    playerState: {
                        ...state.playerState,
                        progress: {
                            ...state.playerState.progress,
                        },
                        levels: {
                            ...state.playerState.levels,
                            [state.levelState.levelType]: levels,
                        },
                    },
                };

                sendEvent(MissionTrackingEventName.missionStarted, state);
            }
            break;
        case getType(LevelActions.setLevelLeaderboard):
            state = {
                ...state,
                levelState: {
                    ...state.levelState,
                    leaderboard: action.payload,
                },
            };
            break;
        case getType(LevelActions.showGoldCardTutorial):
            state = LevelStateHelper.setStateTaskData(state, {
                task: LevelTask.FTUX_GOLD,
            });
            break;
    }

    if (state.levelState.status === LevelStatus.ACTIVE) {
        const previousState = { ...state };

        // Handle active level state actions.
        state = cardReducer(state, action);

        state = checkHelperFriendVisible(state);

        // Update clear cards, jokers and streaks quests.
        state = LevelStateHelper.updateQuestProgress(state, previousState);

        // TODO: combine this with the code that applies the booster
        /* state = {
            ...state,
            playerState: {
                ...state.playerState,
                inventory: {
                    ...state.playerState.inventory,
                    [BoosterType.PLUS_ONE]: 0,
                },
            },
        }; */

        // Update the booster prices
        state = LevelStateHelper.getBoosterPrices(state);
        // Check for level complete.
        if (isLevelComplete(state.levelState)) {
            track('mScoreBeforeEndSequence', state.levelState.score);
            track('mMovesLeft', state.levelState.cardsState.inStack.length);

            // TODO: Use prio to filter correct tasks
            const FTUXtask = FTUX_LEVEL_TASKS.find(ftux => ftux.condition(state));

            if (FTUXtask) {
                state = {
                    ...state,
                    playerState: FTUXStateHelper.updateFTUXProgress(state.playerState, [FTUXtask.type]),
                };
            }

            const levelData = ProgressStateHelper.getLevelData(
                state,
                state.levelState.levelNumber,
                state.levelState.levelType
            );
            const firstComplete: boolean = !levelData || !levelData.stars;

            // Calculate end of level totals
            state = LevelStateHelper.levelComplete(state);

            if (state.UIState.popupID) {
                state = {
                    ...state,
                    UIState: UIStateHelper.hidePopup(state.UIState, state.UIState.popupID, {}),
                };
            }
        }
    }

    return state;
};
